import { getUrlParameter } from "bm-ui-kit";
import clsx from "classnames";
import { type PropsWithChildren, forwardRef, useCallback, useMemo, useState } from "react";
import { SmallCalculatorContext } from "./SmallCalculatorContext";
import type { SmallCalculatorContextType } from "./SmallCalculatorContext.type";

interface Props<StepIds extends string, StepValues extends Record<StepIds, any>> {
	id?: string;
	className?: string;
	classNames?: {
		card?: string;
	};

	initialStep: StepIds;

	bottomLogo?: boolean;
	colorScheme?: "dark" | "light";

	children: React.ReactNode;

	// Callbacks
	onStep: (stepId: StepIds, stepValues: Partial<StepValues>) => Promise<StepIds>;
	onChange?: (stepId: StepIds, stepValues: Partial<StepValues>) => void;
	onError?: (stepId: StepIds, error: Error) => void;
}

export const SmallCalculatorContainer = forwardRef(
	<StepIds extends string, StepValues extends Record<StepIds, any>>(
		{
			id,
			className,
			classNames,
			initialStep,
			bottomLogo,
			colorScheme,
			children,
			onStep,
			onChange,
			onError,
		}: PropsWithChildren<Props<StepIds, StepValues>>,
		ref,
	) => {
		const [isLoading, setIsLoading] = useState(false);
		const [stepHistory, setStepHistory] = useState<StepIds[]>([initialStep]);
		const [stepValues, setStepValues] = useState<Partial<StepValues>>({});
		const finalColorScheme = useMemo(() => {
			return getUrlParameter("colorScheme", colorScheme) === "light" ? "light" : "dark";
		}, [colorScheme]);

		const activeStep = stepHistory[stepHistory.length - 1];

		const handleStep = useCallback(
			async <StepId extends StepIds>(stepId: StepId, data: StepValues[StepId]): Promise<void> => {
				const newStepValues = { ...stepValues, [stepId]: data };
				setStepValues(newStepValues);
				setIsLoading(true);
				try {
					const nextStep = await onStep(stepId, newStepValues);
					if (nextStep !== stepId) {
						setStepHistory((prev) => [...prev, nextStep]);
					}
				} catch (error) {
					onError?.(stepId, error as Error);
					console.error("Error in SmallCalculatorContainer:", error);
				} finally {
					setIsLoading(false);
				}
			},
			[stepValues, onStep, onError],
		);

		const handleChange = useCallback(
			<StepId extends StepIds>(stepId: StepId, data: StepValues[StepId]) => {
				const newStepValues = { ...stepValues, [stepId]: data };
				setStepValues(newStepValues);

				if (onChange) {
					onChange(stepId, newStepValues);
				}
			},
			[stepValues, onChange],
		);

		const handleGoBack = useCallback(() => {
			if (stepHistory.length > 1) {
				setStepHistory((prev) => {
					const newHistory = [...prev];
					newHistory.pop();
					return newHistory;
				});
			}
		}, [stepHistory.length]);

		const contextValue = useMemo<SmallCalculatorContextType<StepIds, StepValues>>(
			() => ({
				onStep: handleStep,
				onChange: handleChange,
				goBack: handleGoBack,
				onError,
				initialStep,
				bottomLogo,
				stepHistory,
				colorScheme,
				activeStep,
				isLoading,
				stepValues,
				classNames,
			}),
			[
				handleStep,
				handleChange,
				handleGoBack,
				onError,
				initialStep,
				bottomLogo,
				stepHistory,
				colorScheme,
				activeStep,
				isLoading,
				stepValues,
				classNames,
			],
		);

		return (
			<SmallCalculatorContext.Provider value={contextValue}>
				<div ref={ref} className={clsx("small-calculator", `small-calculator-${finalColorScheme}`, className)}>
					<div className="small-calculator-container">
						<div className="small-calculator-step-container" id={id}>
							{children}
						</div>
					</div>
				</div>
			</SmallCalculatorContext.Provider>
		);
	},
);
